import { CircleAlert, Clock, Eye } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { WidgetCardHeader } from "@/components/chat/WidgetCardHeader"

/**
 * The data a WidgetTaskOngoing shows. Presentational only — the caller owns
 * where these values come from and what the icons do.
 */
export type WidgetTaskOngoingData = {
  /** Task name / reference — the card title, e.g. "ZL-2847". */
  taskRef: string
  /** When this update was captured, e.g. "dziś 14:20" or "wt. 09:05". */
  updateLabel: string
  /** The stage the driver is on right now, e.g. "W trasie → Köln". */
  currentStage: string
  /** Whether the driver is currently off the planned route. Omit when there
   * is no detour signal — the badge disappears rather than defaulting to
   * on-route. */
  offRoute?: boolean
  /** Minutes vs. schedule — positive = running behind (shown red, e.g.
   * "+5 min"), negative = ahead of schedule (green). Also colours the
   * progress bar. Omit when there's no schedule read. */
  scheduleDeltaMinutes?: number
  /** The next stage, e.g. "Przerwa 45 min". */
  nextStage?: string
  /** ETA to the next stage / stop, e.g. "15:40". */
  nextEta?: string
  /** 0–100 — how far through the task the driver is. */
  progressPercent: number
}

export type WidgetTaskOngoingProps = WidgetTaskOngoingData & {
  /** Opens the task in the widget panel (the "tools" window). */
  onOpenInPanel?: () => void
  className?: string
}

const BADGE =
  "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium"
const GREEN =
  "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
const RED = "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"

/**
 * WidgetTaskOngoing — a single task update the Slick AI agent drops into the
 * thread at a given moment: when the update is from, the stage the driver is
 * on, an on-route / off-route badge, a schedule badge (+/− minutes vs. plan),
 * a progress bar (green on time / red when behind), and what's next with an
 * ETA. The full stage timeline lives in the widget panel, not here. A white
 * shadcn card.
 */
export const WidgetTaskOngoing = ({
  taskRef,
  updateLabel,
  currentStage,
  offRoute,
  scheduleDeltaMinutes,
  nextStage,
  nextEta,
  progressPercent,
  onOpenInPanel,
  className,
}: WidgetTaskOngoingProps) => {
  const { t } = useTranslation()
  const percent = Math.max(0, Math.min(100, Math.round(progressPercent)))
  const hasNext = Boolean(nextStage || nextEta)

  const delta =
    scheduleDeltaMinutes != null ? Math.round(scheduleDeltaMinutes) : null
  const behind = (delta ?? 0) > 0

  return (
    <Card className={cn("w-full max-w-md shadow-none", className)}>
      <WidgetCardHeader
        title={taskRef}
        dateLabel={t("chat.taskOngoing.update", { time: updateLabel })}
        onOpenInPanel={onOpenInPanel}
      />

      <CardContent className="space-y-3 p-4 pt-0">
        <div className="flex items-start justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-semibold text-foreground">
            {currentStage}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            {offRoute != null && (
              <span className={cn(BADGE, offRoute ? RED : GREEN)}>
                {offRoute ? (
                  <>
                    <CircleAlert className="h-3 w-3" aria-hidden />
                    {t("chat.taskOngoing.offRoute")}
                  </>
                ) : (
                  <>
                    <Eye className="h-3 w-3" aria-hidden />
                    {t("chat.taskOngoing.onRoute")}
                  </>
                )}
              </span>
            )}
            {delta != null && delta !== 0 && (
              <span className={cn(BADGE, behind ? RED : GREEN)}>
                <Clock className="h-3 w-3" aria-hidden />
                {behind ? "+" : "−"}
                {Math.abs(delta)} min
              </span>
            )}
          </div>
        </div>

        <Progress
          value={percent}
          className={cn(
            "h-1.5",
            behind ? "[&>div]:bg-red-500" : "[&>div]:bg-emerald-500",
          )}
        />

        {hasNext && (
          <div className="truncate text-sm text-muted-foreground">
            <span className="font-medium">{t("chat.taskOngoing.next")}</span>{" "}
            {nextStage}
            {nextStage && nextEta ? " · " : ""}
            {nextEta ? `ETA ${nextEta}` : ""}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default WidgetTaskOngoing
